import { useEffect, useRef, useState } from 'react';
import { useWebSocketLazy } from '~/helpers/wsConnection';
import { State } from '../types';
import { OperationType, StatusType } from '../../StatusCard';
import { verifyConnection, verifyOperation, verifyStatus } from '../helpers';
import { useDebounce } from '~/hooks';

export function useWebsocketStatusByMachine(
  deviceCode: string | undefined,
): {
  status: StatusType | undefined;
  operation: OperationType | undefined;
  connection: boolean;
  loading: boolean;
  error: boolean;
} {
  const isDAM = deviceCode?.startsWith('DAM');
  const isDUT = deviceCode?.startsWith('DUT');
  const isDRI = deviceCode?.startsWith('DRI');

  const firstRender = useRef(true);
  const websocketLazy = useWebSocketLazy();

  const [rssi, setRssi] = useState();
  const [operation, setOperation] = useState<State>('forbid');
  const [connection, setConnection] = useState<'ONLINE' | 'OFFLINE'>('OFFLINE');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const rssiDebounced = useDebounce(rssi, 100);
  const operationDebounced = useDebounce(operation, 100);
  const connectionDebounced = useDebounce(connection, 100);
  const loadingDebounced = useDebounce(loading, 100);

  function onWsOpen(wsConn): void {
    if (isDAM) {
      wsConn.send({ type: 'subscribeStatus', data: { dam_id: deviceCode } });
      wsConn.send({ type: 'damStatus', data: { dam_id: deviceCode } });
    }
    else if (isDUT) {
      wsConn.send({ type: 'dutSubscribeRealTime', data: { DUT_ID: deviceCode } });
    }
    else if (isDRI) {
      wsConn.send({ type: 'driSubscribeRealTime', data: { DRI_ID: deviceCode } });
    }
  }

  function onWsMessage(payload): void {
    if (!payload) return;
    if (payload.data.dev_id !== deviceCode) return;

    if (payload.data.status) {
      setConnection(payload.data.status);
    }

    if (payload.data.State) {
      setOperation(payload.data.State);
    }

    if (payload.data.RSSI) {
      setRssi(payload.data.RSSI);
    }

    if (loading) {
      setLoading(false);
    }
  }

  function beforeWsClose(wsConn): void {
    if (isDAM) {
      wsConn.send({ type: 'unsubscribeStatus' });
    }
    if (isDUT) {
      wsConn.send(JSON.stringify({ type: 'dutUnsubscribeRealTime', data: {} }));
    }
    if (isDRI) {
      wsConn.send({ type: 'driUnsubscribeRealTime' });
    }
  }

  function onError(): void {
    setError(true);

    if (loading) {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (deviceCode && firstRender.current) {
      firstRender.current = false;
      websocketLazy.start(onWsOpen, onWsMessage, beforeWsClose, onError);
    }
  }, [deviceCode]);

  return {
    loading: loadingDebounced,
    connection: verifyConnection(rssiDebounced, connectionDebounced),
    operation: verifyOperation({
      operation: operationDebounced,
      rssi: rssiDebounced,
      status: connectionDebounced,
    }),
    status: verifyStatus({
      operation: operationDebounced,
      rssi: rssiDebounced,
      status: connectionDebounced,
    }, !!isDAM),
    error,
  };
}
